import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/Bits";
import { STAGE_META, date } from "@/lib/format";
import { MapPin } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { canEdit } from "@/lib/format";
import TeamActivity from "@/components/TeamActivity";

const COLS = ["awaiting_work", "in_progress", "blocked", "ready_for_inspection", "ready_for_delivery", "completed"];

export default function Board() {
  const [cols, setCols] = useState(null);
  const [loc, setLoc] = useState("");
  const [view, setView] = useState("board");
  const { user } = useAuth();
  const team = canEdit(user.role);

  const load = useCallback(() => {
    api.get("/board", { params: loc ? { location: loc } : {} }).then((r) => setCols(r.data)).catch(() => {});
  }, [loc]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <PageHeader title="Production Board" subtitle={view === "team" ? "Who is on what right now, and time tracked per job" : "Awaiting → In Progress → Blocked → Inspection → Delivery → Complete"}>
        {team && (
          <div className="flex gap-1 p-1 rounded-lg bg-accent/60" data-testid="board-view-toggle">
            {[["board", "Task Board"], ["team", "Team Activity"]].map(([k, l]) => (
              <button key={k} onClick={() => setView(k)} data-testid={`board-view-${k}`}
                className={`px-3 py-1.5 rounded-md text-xs font-bold uppercase transition-colors ${view === k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
            ))}
          </div>
        )}
        {view === "board" && <div className="flex gap-1">
          {["", "Rathcairn", "Athboy"].map((l) => (
            <button key={l || "all"} onClick={() => setLoc(l)} data-testid={`board-filter-${l || "all"}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                loc === l ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground"
              }`}>
              {l || "All Sites"}
            </button>
          ))}
        </div>}
      </PageHeader>

      {view === "team" ? <TeamActivity /> : !cols ? (
        <div className="text-muted-foreground">Loading board…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-4">
          {COLS.map((c) => (
            <div key={c} className="flex flex-col" data-testid={`board-col-${c}`}>
              <div className={`px-3 py-2 rounded-t-lg border text-xs font-heading font-bold uppercase tracking-wide flex items-center justify-between ${STAGE_META[c].cls}`}>
                {STAGE_META[c].label}
                <span className="font-mono-num">{(cols[c] || []).length}</span>
              </div>
              <div className="flex-1 space-y-2 p-2 bg-card/40 border border-t-0 border-border rounded-b-lg min-h-[120px]">
                {(cols[c] || []).map((s) => (
                  <Link key={s.id} to={`/jobs/${s.job_id}`} data-testid={`board-card-${s.id}`}
                    className="block p-2.5 rounded-lg bg-card border border-border hover:border-primary/50 transition-colors">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground truncate">{s.customer_name}</span>
                      {s.batch_label && <span className="text-[10px] px-1.5 py-0.5 rounded bg-accent text-muted-foreground">{s.batch_label}</span>}
                    </div>
                    <div className="text-xs font-semibold truncate">{s.job_name || s.job_number}</div>
                    <div className="text-sm font-semibold mt-1 text-primary">{s.name}</div>
                    <div className="font-mono-num text-[10px] text-muted-foreground">{s.job_number}</div>
                    <div className="flex items-center gap-3 mt-2 text-[11px] text-muted-foreground">
                      <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{s.location}</span>
                      <span>Qty {s.qty_completed}/{s.qty_total}</span>
                    </div>
                    {s.hold_reason && <div className="mt-1 text-[11px] text-red-400 truncate">⚠ {s.hold_reason}</div>}
                    {s.due_date && <div className="mt-1 text-[10px] text-muted-foreground">Due {date(s.due_date)}</div>}
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
